import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  Call as CallIcon,
  QrCode as QrCodeIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Language as WebIcon,
  LocationOn as LocationIcon,
  AccessTime as ClockIcon,
  Print as FaxIcon,
  WhatsApp as WhatsAppIcon,
  Map as MapIcon,
  Payment as PaymentIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { sortCardSections } from '../utils/sortCardSections';
import { toDisplayCard } from '../utils/cardDataStructure';
import { normalizeGalleryLayout } from '../utils/galleryLayout';
import BannerStrip from '../components/BannerStrip';
import GalleryImageMosaic from '../components/GalleryImageMosaic';
import { TitleCompanyStack } from '../components/TitleCompanyStack';
import { AboutFormattedText } from '../components/AboutFormattedText';
import { stripAboutFormatting } from '../utils/aboutPlainText';

const getSocialIcon = (platform) => {
  const s = (platform || '').toLowerCase();
  if (s === 'linkedin') return <LinkedInIcon sx={{ color: 'white', fontSize: 22 }} />;
  if (s === 'twitter' || s === 'x') return <TwitterIcon sx={{ color: 'white', fontSize: 22 }} />;
  if (s === 'instagram') return <InstagramIcon sx={{ color: 'white', fontSize: 22 }} />;
  if (s === 'facebook') return <FacebookIcon sx={{ color: 'white', fontSize: 22 }} />;
  return <ShareIcon sx={{ color: 'white', fontSize: 22 }} />;
};

const getSocialColor = (platform) => {
  const s = (platform || '').toLowerCase();
  if (s === 'linkedin') return '#0077b5';
  if (s === 'twitter' || s === 'x') return '#1da1f2';
  if (s === 'instagram') return '#e4405f';
  if (s === 'facebook') return '#1877f2';
  return '#6b7280';
};

const initials = (name) => (name || 'YN').split(/\s+/).map(s => s[0]).join('').toUpperCase().slice(0, 2);

const ContactActionSheet = ({ open, onClose, type, value }) => {
  const [copied, setCopied] = React.useState(false);
  if (!open) return null;
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const isPhone = type === 'phone';
  return (
    <Box onClick={onClose} sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <Box onClick={e => e.stopPropagation()} sx={{ width: '100%', maxWidth: 420, bgcolor: 'white', borderRadius: '16px 16px 0 0', p: 2.5, pb: 3, animation: 'slideUp 0.2s ease-out' }}>
        <Box sx={{ width: 36, height: 4, bgcolor: '#ddd', borderRadius: 2, mx: 'auto', mb: 2 }} />
        <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 0.5, color: '#64748b', fontSize: '0.75rem' }}>
          {isPhone ? 'Phone Number' : 'Email Address'}
        </Typography>
        <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 700, mb: 2.5, wordBreak: 'break-all' }}>{value}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button fullWidth variant="contained" startIcon={isPhone ? <CallIcon /> : <EmailIcon />}
            href={isPhone ? `tel:${value}` : `mailto:${value}`}
            sx={{ borderRadius: 2, py: 1.25, fontWeight: 600, textTransform: 'none', bgcolor: isPhone ? '#22c55e' : '#3b82f6', '&:hover': { bgcolor: isPhone ? '#16a34a' : '#2563eb' }, boxShadow: 'none' }}>
            {isPhone ? 'Call' : 'Send Email'}
          </Button>
          <Button fullWidth variant="outlined" startIcon={copied ? <CheckIcon /> : <CopyIcon />}
            onClick={handleCopy}
            sx={{ borderRadius: 2, py: 1.25, fontWeight: 600, textTransform: 'none', borderColor: '#e2e8f0', color: copied ? '#22c55e' : '#64748b', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </Box>
      </Box>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
    </Box>
  );
};

const WhatsAppButton = ({ number, accent }) => {
  if (!number) return null;
  const clean = number.replace(/[^0-9+]/g, '');
  return (
    <Box onClick={() => window.open(`https://wa.me/${clean}`, '_blank')}
      sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: '#dcfce7', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: '#bbf7d0', transform: 'translateY(-1px)' } }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
        <WhatsAppIcon sx={{ color: 'white', fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#166534', fontSize: '0.85rem' }}>Chat on WhatsApp</Typography>
        <Typography variant="caption" sx={{ color: '#15803d' }}>{number}</Typography>
      </Box>
      <Typography sx={{ color: '#15803d', fontSize: '1.2rem' }}>&#8250;</Typography>
    </Box>
  );
};

const GoogleMapsButton = ({ url }) => {
  if (!url) return null;
  return (
    <Box onClick={() => window.open(url, '_blank')}
      sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', cursor: 'pointer', transition: 'all 0.2s', '&:hover': { bgcolor: '#dbeafe', transform: 'translateY(-1px)' } }}>
      <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: '#4285F4', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
        <MapIcon sx={{ color: 'white', fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.85rem' }}>View on Google Maps</Typography>
        <Typography variant="caption" sx={{ color: '#3b82f6' }}>Open directions</Typography>
      </Box>
      <Typography sx={{ color: '#3b82f6', fontSize: '1.2rem' }}>&#8250;</Typography>
    </Box>
  );
};

const PAYMENT_METHODS = [
  { key: 'gpay', label: 'Google Pay', color: '#4285F4', icon: '₹', bgLight: '#e8f0fe' },
  { key: 'phonepe', label: 'PhonePe', color: '#5f259f', icon: '₹', bgLight: '#f3e8ff' },
  { key: 'paytm', label: 'Paytm', color: '#00BAF2', icon: '₹', bgLight: '#e0f7ff' },
  { key: 'upiId', label: 'UPI', color: '#4CAF50', icon: '₹', bgLight: '#e8f5e9' },
];

const PaymentLinksSection = ({ cardData, titleColor = '#2d3748', textColor = '#4a5568', align = 'center' }) => {
  const methods = PAYMENT_METHODS.filter(m => cardData[m.key]);
  if (!methods.length) return null;

  const handleCopyPayment = (value, label) => {
    navigator.clipboard.writeText(value).then(() => alert(`${label} ID copied!`));
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align, display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : 'flex-start', gap: 1 }}>
        <PaymentIcon sx={{ fontSize: 20 }} /> Pay Me
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {methods.map(m => {
          const val = cardData[m.key];
          const isUPI = val.includes('@') || m.key === 'upiId';
          const upiLink = isUPI ? `upi://pay?pa=${encodeURIComponent(val)}&pn=${encodeURIComponent(cardData.name || '')}` : null;

          return (
            <Box key={m.key} sx={{ display: 'flex', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: m.bgLight, transition: 'all 0.2s', '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>{m.icon}</Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: m.color, fontSize: '0.85rem' }}>{m.label}</Typography>
                <Typography variant="caption" sx={{ color: textColor, wordBreak: 'break-all' }}>{val}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {upiLink && (
                  <Box component="a" href={upiLink}
                    sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: m.color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textDecoration: 'none' }}>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem' }}>Pay</Typography>
                  </Box>
                )}
                <Box onClick={() => handleCopyPayment(val, m.label)}
                  sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}>
                  <CopyIcon sx={{ fontSize: 14, color: textColor }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Shared sections used by all layouts (order follows cardData.sections[].order)
const SharedSections = ({ cardData, accent, titleColor = '#2d3748', textColor = '#4a5568', borderColor = '#e2e8f0', align = 'center', onSaveContact }) => {
  const sections = cardData.sections || [];
  const enabledSocials = (cardData.socialLinks || []).filter(l => l.enabled && l.url);
  const [actionSheet, setActionSheet] = React.useState({ open: false, type: null, value: '' });

  const renderContactSection = (key) => {
    const fullAddress = [cardData.address, cardData.city, cardData.state, cardData.country].filter(Boolean).join(', ');
    const contactRows = [
      cardData.phone && { icon: <PhoneIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#22c55e', text: cardData.phone, action: () => setActionSheet({ open: true, type: 'phone', value: cardData.phone }) },
      cardData.additionalPhone && { icon: <PhoneIcon sx={{ color: 'white', fontSize: 14 }} />, bg: accent, text: cardData.additionalPhone, action: () => setActionSheet({ open: true, type: 'phone', value: cardData.additionalPhone }) },
      cardData.email && { icon: <EmailIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#3b82f6', text: cardData.email, action: () => setActionSheet({ open: true, type: 'email', value: cardData.email }) },
      cardData.website && { icon: <WebIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#2196F3', text: cardData.website, href: cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}` },
      fullAddress && { icon: <LocationIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#9333ea', text: fullAddress, href: cardData.googleMapsUrl || undefined },
      cardData.fax && { icon: <FaxIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#6b7280', text: cardData.fax },
      cardData.officeHours && { icon: <ClockIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#f59e0b', text: cardData.officeHours },
    ].filter(Boolean);
    if (!contactRows.length) return null;
    return (
      <Box key={key} sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Contact</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {contactRows.map((item, i) => (
            <Box key={i}
              component={item.href ? 'a' : 'div'}
              href={item.href || undefined}
              target={item.href?.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              onClick={item.action || undefined}
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: (item.action || item.href) ? 'pointer' : 'default', '&:hover': { opacity: 0.8 } }}>
              <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>{item.icon}</Box>
              <Typography variant="body2" sx={{ color: textColor, wordBreak: 'break-word' }}>{item.text}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const orderedBlocks = sortCardSections(sections).flatMap((section) => {
    if (!section.enabled || section.id === 'basic-info') return [];
    switch (section.id) {
      case 'about':
      case 'about-section':
        if (!cardData.about) return [];
        return [
          <Box key={section.id} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>About</Typography>
            <AboutFormattedText text={cardData.about} variant="body2" textAlign={align} sx={{ color: textColor }} />
          </Box>,
        ];
      case 'contact':
      case 'contact-info': {
        const el = renderContactSection(section.id);
        return el ? [el] : [];
      }
      case 'social':
      case 'social-links':
        if (!enabledSocials.length) return [];
        return [
          <Box key={section.id} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Connect</Typography>
            <Box sx={{ display: 'flex', justifyContent: align === 'center' ? 'center' : 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
              {enabledSocials.map((link, i) => (
                <Box key={link.id || i} onClick={() => window.open(link.url, '_blank')}
                  sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: getSocialColor(link.platform), display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'transform 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-2px)' } }}>
                  {getSocialIcon(link.platform)}
                </Box>
              ))}
            </Box>
          </Box>,
        ];
      case 'appointment':
        if (!cardData.calendarUrl) return [];
        return [
          <Box key={section.id} sx={{ mb: 3 }}>
            <Button fullWidth variant="contained" component="a" href={cardData.calendarUrl} target="_blank" rel="noopener noreferrer"
              sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: accent, boxShadow: 'none', '&:hover': { bgcolor: accent, opacity: 0.92 } }}>
              {cardData.appointmentText || 'Book Appointment'}
            </Button>
          </Box>,
        ];
      case 'custom-sections':
        return (cardData.customSections || [])
          .filter((c) => c.enabled && (c.title || c.description))
          .map((c, idx) => (
            <Box key={`cust-sec-${idx}`} sx={{ mb: 3 }}>
              {c.title && <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>{c.title}</Typography>}
              {c.description && <Typography variant="body2" sx={{ color: textColor, textAlign: align, lineHeight: 1.7 }}>{c.description}</Typography>}
            </Box>
          ));
      case 'custom-links': {
        const custLinks = (cardData.customLinks || []).filter((l) => l.enabled && l.title && l.url);
        if (!custLinks.length) return [];
        return [
          <Box key="cust-links-pub" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Useful Links</Typography>
            {custLinks.map((link, i) => (
              <Box key={i} onClick={() => window.open(link.url, '_blank')}
                sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1.5, border: `1px solid ${borderColor}`, borderRadius: 1, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: accent, boxShadow: `0 2px 8px ${accent}20` } }}>
                <WebIcon sx={{ fontSize: 18, color: accent, mr: 1.5 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: titleColor }}>{link.title}</Typography>
                  <Typography variant="caption" sx={{ color: textColor }}>{link.url}</Typography>
                </Box>
                <Typography sx={{ color: textColor }}>&#8250;</Typography>
              </Box>
            ))}
          </Box>,
        ];
      }
      case 'gallery': {
        const gs = cardData.gallerySection;
        const gImages = gs?.images?.filter((img) => img.url);
        if (!gImages?.length) return [];
        const gl = normalizeGalleryLayout(gs?.layout);
        return [
          <Box key="gallery-pub" sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Gallery</Typography>
            <GalleryImageMosaic
              images={gImages}
              layout={gl}
              imgMinHeight={140}
              gap={1.5}
              captionFontSize="0.75rem"
              onImageClick={(url) => window.open(url, '_blank')}
              enlargeOnHover
            />
          </Box>,
        ];
      }
      default:
        if (section.id.startsWith('custom-') && section.id !== 'custom-sections' && section.id !== 'custom-links') {
          if (!(cardData[`${section.id}_header`] || cardData[`${section.id}_description`])) return [];
          return [
            <Box key={section.id} sx={{ mb: 3 }}>
              {cardData[`${section.id}_header`] && <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>{cardData[`${section.id}_header`]}</Typography>}
              {cardData[`${section.id}_description`] && <Typography variant="body2" sx={{ color: textColor, textAlign: align, lineHeight: 1.7 }}>{cardData[`${section.id}_description`]}</Typography>}
            </Box>,
          ];
        }
        if (section.id.startsWith('links-')) {
          const links = cardData[`${section.id}_links`]?.filter(l => l.title && l.url);
          if (!links?.length) return [];
          return [
            <Box key={section.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Useful Links</Typography>
              {links.map((link, i) => (
                <Box key={i} onClick={() => window.open(link.url, '_blank')}
                  sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1.5, border: `1px solid ${borderColor}`, borderRadius: 1, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: accent, boxShadow: `0 2px 8px ${accent}20` } }}>
                  <WebIcon sx={{ fontSize: 18, color: accent, mr: 1.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: titleColor }}>{link.title}</Typography>
                    <Typography variant="caption" sx={{ color: textColor }}>{link.url}</Typography>
                  </Box>
                  <Typography sx={{ color: textColor }}>&#8250;</Typography>
                </Box>
              ))}
            </Box>,
          ];
        }
        if (section.id.startsWith('images-')) {
          const images = cardData[`${section.id}_images`]?.filter(img => img.url);
          if (!images?.length) return [];
          const layout = cardData[`${section.id}_galleryLayout`];
          return [
            <Box key={section.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, color: titleColor, textAlign: align }}>Gallery</Typography>
              <GalleryImageMosaic
                images={images}
                layout={layout}
                imgMinHeight={140}
                gap={1.5}
                captionFontSize="0.75rem"
                onImageClick={(url) => window.open(url, '_blank')}
                enlargeOnHover
              />
            </Box>,
          ];
        }
        return [];
    }
  });

  return (
    <>
      {orderedBlocks}

      <WhatsAppButton number={cardData.whatsapp} accent={accent} />
      {cardData.whatsapp && <Box sx={{ mb: 1.5 }} />}

      <GoogleMapsButton url={cardData.googleMapsUrl} />
      {cardData.googleMapsUrl && <Box sx={{ mb: 1.5 }} />}

      <PaymentLinksSection cardData={cardData} titleColor={titleColor} textColor={textColor} align={align} />

      <ContactActionSheet open={actionSheet.open} onClose={() => setActionSheet({ open: false, type: null, value: '' })} type={actionSheet.type} value={actionSheet.value} />

      {onSaveContact && (
        <Box sx={{ mb: 2 }}>
          <Button fullWidth variant="contained" startIcon={<PersonAddIcon />}
            onClick={onSaveContact}
            sx={{
              borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem',
              bgcolor: accent, boxShadow: `0 4px 14px ${accent}40`,
              '&:hover': { bgcolor: accent, opacity: 0.9, boxShadow: `0 6px 20px ${accent}50` },
            }}>
            Save Contact
          </Button>
        </Box>
      )}
    </>
  );
};

const PersonalCardContent = ({ cardData, cfg, layoutType, accent, handleSaveContact }) => {
  const name = cardData.name || 'Your Name';
  const title = cardData.title || '';
  const company = cardData.company || '';
  const ini = initials(name);

  if (layoutType === 'dark') {
    const textPrimary = cfg.textPrimary || '#ffffff';
    const textSecondary = cfg.textSecondary || '#94a3b8';
    const textMuted = cfg.textMuted || '#64748b';
    const cardBg = cfg.cardBg || '#1e293b';
    const surfaceBg = `${textMuted}18`;
    const enabledSocials = (cardData.socialLinks || []).filter(l => l.enabled && l.url);
    const [darkActionSheet, setDarkActionSheet] = React.useState({ open: false, type: null, value: '' });

    return (
      <Box sx={{ bgcolor: cardBg }}>
        {cardData.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} fallbackBg={cardBg} />}

        <Box sx={{ textAlign: 'center', pt: 4, pb: 2, px: 3 }}>
          <Avatar src={cardData.profileImageUrl || undefined}
            sx={{ width: 100, height: 100, mx: 'auto', mb: 2, border: `3px solid ${accent}55`, bgcolor: `${accent}30`, color: accent, fontSize: '2.2rem', fontWeight: 700, boxShadow: `0 0 30px ${accent}20` }}>
            {!cardData.profileImageUrl && ini}
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 700, color: textPrimary, lineHeight: 1.2, letterSpacing: '-0.01em' }}>{name}</Typography>
          <TitleCompanyStack
            title={title}
            company={company}
            boxSx={{ mt: 0.75, textAlign: 'center' }}
            titleSx={{ fontSize: '0.875rem', color: accent, fontWeight: 600, letterSpacing: '0.04em', lineHeight: 1.35 }}
            companySx={{ fontSize: '0.875rem', color: textSecondary, fontWeight: 500, letterSpacing: '0.03em', lineHeight: 1.35 }}
          />
        </Box>

        <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {sortCardSections(cardData.sections || []).flatMap((section) => {
            if (!section.enabled || section.id === 'basic-info') return [];
            switch (section.id) {
              case 'about':
              case 'about-section':
                if (!cardData.about) return [];
                return [
                  <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>About</Typography>
                    <AboutFormattedText
                      text={cardData.about}
                      variant="body2"
                      sx={{ color: textSecondary }}
                      markSx={{ backgroundColor: 'rgba(250, 204, 21, 0.28)' }}
                    />
                  </Box>,
                ];
              case 'contact':
              case 'contact-info': {
                const fullAddress = [cardData.address, cardData.city, cardData.state, cardData.country].filter(Boolean).join(', ');
                const contactRows = [
                  cardData.phone && { icon: <PhoneIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#22c55e', text: cardData.phone, action: () => setDarkActionSheet({ open: true, type: 'phone', value: cardData.phone }) },
                  cardData.additionalPhone && { icon: <PhoneIcon sx={{ color: 'white', fontSize: 14 }} />, bg: accent, text: cardData.additionalPhone, action: () => setDarkActionSheet({ open: true, type: 'phone', value: cardData.additionalPhone }) },
                  cardData.email && { icon: <EmailIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#3b82f6', text: cardData.email, action: () => setDarkActionSheet({ open: true, type: 'email', value: cardData.email }) },
                  cardData.website && { icon: <WebIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#2196F3', text: cardData.website, href: cardData.website.startsWith('http') ? cardData.website : `https://${cardData.website}` },
                  fullAddress && { icon: <LocationIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#9333ea', text: fullAddress, href: cardData.googleMapsUrl || undefined },
                  cardData.fax && { icon: <FaxIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#6b7280', text: cardData.fax },
                  cardData.officeHours && { icon: <ClockIcon sx={{ color: 'white', fontSize: 14 }} />, bg: '#f59e0b', text: cardData.officeHours },
                ].filter(Boolean);
                if (!contactRows.length) return [];
                return [
                  <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>Contact</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {contactRows.map((item, i) => (
                        <Box key={i}
                          component={item.href ? 'a' : 'div'}
                          href={item.href || undefined}
                          target={item.href?.startsWith('http') ? '_blank' : undefined}
                          rel="noopener noreferrer"
                          onClick={item.action || undefined}
                          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', cursor: (item.action || item.href) ? 'pointer' : 'default', '&:hover': { opacity: 0.8 } }}>
                          <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.5, flexShrink: 0 }}>{item.icon}</Box>
                          <Typography variant="body2" sx={{ color: textSecondary, wordBreak: 'break-word' }}>{item.text}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>,
                ];
              }
              case 'social':
              case 'social-links':
                if (!enabledSocials.length) return [];
                return [
                  <Box key={section.id} sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {enabledSocials.map((link, i) => (
                      <Box key={link.id || i} onClick={() => window.open(link.url, '_blank')}
                        sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: getSocialColor(link.platform), display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: `0 3px 12px ${getSocialColor(link.platform)}40`, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
                        {getSocialIcon(link.platform)}
                      </Box>
                    ))}
                  </Box>,
                ];
              case 'appointment':
                if (!cardData.calendarUrl) return [];
                return [
                  <Box key={section.id}>
                    <Button fullWidth variant="contained" component="a" href={cardData.calendarUrl} target="_blank" rel="noopener noreferrer"
                      sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', bgcolor: accent, boxShadow: `0 4px 14px ${accent}40`, '&:hover': { bgcolor: accent, opacity: 0.9 } }}>
                      {cardData.appointmentText || 'Book Appointment'}
                    </Button>
                  </Box>,
                ];
              case 'custom-sections':
                return (cardData.customSections || [])
                  .filter((c) => c.enabled && (c.title || c.description))
                  .map((c, idx) => (
                    <Box key={`d-cust-${idx}`} sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                      {c.title && <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>{c.title}</Typography>}
                      {c.description && <Typography variant="body2" sx={{ color: textSecondary, lineHeight: 1.7 }}>{c.description}</Typography>}
                    </Box>
                  ));
              case 'custom-links': {
                const dLinks = (cardData.customLinks || []).filter((l) => l.enabled && l.title && l.url);
                if (!dLinks.length) return [];
                return [
                  <Box key="d-cust-links" sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>Useful Links</Typography>
                    {dLinks.map((link, i) => (
                      <Box key={i} onClick={() => window.open(link.url, '_blank')}
                        sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1, border: `1px solid ${textMuted}44`, borderRadius: 1, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: accent } }}>
                        <WebIcon sx={{ fontSize: 18, color: accent, mr: 1.5 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary }}>{link.title}</Typography>
                          <Typography variant="caption" sx={{ color: textSecondary }}>{link.url}</Typography>
                        </Box>
                        <Typography sx={{ color: textSecondary }}>&#8250;</Typography>
                      </Box>
                    ))}
                  </Box>,
                ];
              }
              case 'gallery': {
                const dgs = cardData.gallerySection;
                const dimgs = dgs?.images?.filter((img) => img.url);
                if (!dimgs?.length) return [];
                const dgl = normalizeGalleryLayout(dgs?.layout);
                return [
                  <Box key="d-gallery-pub">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>Gallery</Typography>
                    <GalleryImageMosaic
                      images={dimgs}
                      layout={dgl}
                      imgMinHeight={140}
                      gap={1.5}
                      captionFontSize="0.75rem"
                      onImageClick={(url) => window.open(url, '_blank')}
                      enlargeOnHover
                    />
                  </Box>,
                ];
              }
              default:
                if (section.id.startsWith('custom-') && section.id !== 'custom-sections' && section.id !== 'custom-links') {
                  if (!(cardData[`${section.id}_header`] || cardData[`${section.id}_description`])) return [];
                  return [
                    <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                      {cardData[`${section.id}_header`] && <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>{cardData[`${section.id}_header`]}</Typography>}
                      {cardData[`${section.id}_description`] && <Typography variant="body2" sx={{ color: textSecondary, lineHeight: 1.7 }}>{cardData[`${section.id}_description`]}</Typography>}
                    </Box>,
                  ];
                }
                if (section.id.startsWith('links-')) {
                  const links = cardData[`${section.id}_links`]?.filter(l => l.title && l.url);
                  if (!links?.length) return [];
                  return [
                    <Box key={section.id} sx={{ bgcolor: surfaceBg, borderRadius: 2, p: 2.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>Useful Links</Typography>
                      {links.map((link, i) => (
                        <Box key={i} onClick={() => window.open(link.url, '_blank')}
                          sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1, border: `1px solid ${textMuted}44`, borderRadius: 1, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: accent } }}>
                          <WebIcon sx={{ fontSize: 18, color: accent, mr: 1.5 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textPrimary }}>{link.title}</Typography>
                            <Typography variant="caption" sx={{ color: textSecondary }}>{link.url}</Typography>
                          </Box>
                          <Typography sx={{ color: textSecondary }}>&#8250;</Typography>
                        </Box>
                      ))}
                    </Box>,
                  ];
                }
                if (section.id.startsWith('images-')) {
                  const images = cardData[`${section.id}_images`]?.filter(img => img.url);
                  if (!images?.length) return [];
                  const layout = cardData[`${section.id}_galleryLayout`];
                  return [
                    <Box key={section.id}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.75rem' }}>Gallery</Typography>
                      <GalleryImageMosaic
                        images={images}
                        layout={layout}
                        imgMinHeight={140}
                        gap={1.5}
                        captionFontSize="0.75rem"
                        onImageClick={(url) => window.open(url, '_blank')}
                        enlargeOnHover
                      />
                    </Box>,
                  ];
                }
                return [];
            }
          })}

          {cardData.whatsapp && (
            <WhatsAppButton number={cardData.whatsapp} accent={accent} />
          )}
          {cardData.googleMapsUrl && (
            <GoogleMapsButton url={cardData.googleMapsUrl} />
          )}

          <PaymentLinksSection cardData={cardData} titleColor={textPrimary} textColor={textSecondary} />
        </Box>

        <Box sx={{ px: 3, pb: 2 }}>
          <Button fullWidth variant="contained" startIcon={<PersonAddIcon />}
            onClick={handleSaveContact}
            sx={{ borderRadius: 2, py: 1.5, fontWeight: 700, textTransform: 'none', fontSize: '0.95rem', bgcolor: accent, boxShadow: `0 4px 14px ${accent}40`, '&:hover': { bgcolor: accent, opacity: 0.9 } }}>
            Save Contact
          </Button>
        </Box>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: textMuted, py: 2 }}>Powered by WebCard Digital</Typography>
        <ContactActionSheet open={darkActionSheet.open} onClose={() => setDarkActionSheet({ open: false, type: null, value: '' })} type={darkActionSheet.type} value={darkActionSheet.value} />
      </Box>
    );
  }

  if (layoutType === 'rows') {
    return (
      <Box sx={{ bgcolor: 'white' }}>
        <Box sx={{ height: 5, background: cfg.accentGradient || `linear-gradient(90deg, ${accent}, ${accent}88)` }} />
        {cardData.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} />}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 3 }}>
          <Avatar src={cardData.profileImageUrl || undefined}
            sx={{ width: 80, height: 80, flexShrink: 0, bgcolor: cfg.avatarBg || '#ede9fe', color: cfg.avatarColor || accent, fontWeight: 700, fontSize: '1.5rem' }}>
            {!cardData.profileImageUrl && ini}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{name}</Typography>
            <TitleCompanyStack
              title={title}
              company={company}
              boxSx={{ mt: 0.5 }}
              titleSx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.35 }}
              companySx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.35 }}
            />
          </Box>
        </Box>
        <Box sx={{ px: 3, pb: 3 }}>
          <SharedSections cardData={cardData} accent={accent} align="left" onSaveContact={handleSaveContact} />
        </Box>
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8', py: 2 }}>Powered by WebCard Digital</Typography>
      </Box>
    );
  }

  if (layoutType === 'split') {
    const enabledSocials = (cardData.socialLinks || []).filter(l => l.enabled && l.url);
    return (
      <Box sx={{ display: 'flex', minHeight: 500 }}>
        <Box sx={{ width: 100, background: cfg.sidebarBg || `linear-gradient(180deg, ${accent}, ${accent}dd)`, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, flexShrink: 0 }}>
          <Avatar src={cardData.profileImageUrl || undefined}
            sx={{ width: 60, height: 60, mb: 1.5, bgcolor: 'rgba(255,255,255,0.2)', border: '3px solid rgba(255,255,255,0.4)', color: 'white', fontWeight: 700, fontSize: '1.2rem' }}>
            {!cardData.profileImageUrl && ini}
          </Avatar>
          {enabledSocials.slice(0, 5).map((link, i) => (
            <Box key={i} onClick={() => window.open(link.url, '_blank')}
              sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
              {React.cloneElement(getSocialIcon(link.platform), { sx: { color: 'white', fontSize: 16 } })}
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, bgcolor: 'white', display: 'flex', flexDirection: 'column' }}>
          {cardData.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} />}
          <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>{name}</Typography>
            {title && <Typography variant="body1" sx={{ color: accent, fontWeight: 600, mt: 0.5 }}>{title}</Typography>}
            {company && <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>{company}</Typography>}
            <Box sx={{ mt: 2.5 }}>
              <SharedSections cardData={cardData} accent={accent} align="left" onSaveContact={handleSaveContact} />
            </Box>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8', py: 1.5 }}>Powered by WebCard Digital</Typography>
        </Box>
      </Box>
    );
  }

  // Default: Hero layout
  return (
    <Box sx={{ bgcolor: 'white' }}>
      {cardData.bannerImageUrl && <BannerStrip imageUrl={cardData.bannerImageUrl} />}
      <Box sx={{ background: cfg.headerBg || `linear-gradient(135deg, #102E5C 0%, ${accent} 100%)`, p: 4, textAlign: 'center', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
        <Avatar src={cardData.profileImageUrl || undefined}
          sx={{ width: 110, height: 110, mx: 'auto', mb: 2, border: '4px solid rgba(255,255,255,0.4)', bgcolor: cfg.avatarBg || accent, color: 'white', fontSize: '2.5rem', fontWeight: 700, boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}>
          {!cardData.profileImageUrl && ini}
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{name}</Typography>
        <TitleCompanyStack
          title={title}
          company={company}
          boxSx={{ mt: 0.5, textAlign: 'center', px: 0.5 }}
          titleSx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.85)', lineHeight: 1.35 }}
          companySx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.35 }}
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2, flexWrap: 'wrap' }}>
          {cardData.phone && <Button size="small" startIcon={<CallIcon sx={{ fontSize: 16 }} />} href={`tel:${cardData.phone}`} sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Call</Button>}
          {(cardData.whatsapp || cardData.phone) && <Button size="small" startIcon={<WhatsAppIcon sx={{ fontSize: 16 }} />} href={`https://wa.me/${(cardData.whatsapp || cardData.phone).replace(/[^0-9+]/g, '')}`} target="_blank" sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>WhatsApp</Button>}
          {cardData.email && <Button size="small" startIcon={<EmailIcon sx={{ fontSize: 16 }} />} href={`mailto:${cardData.email}`} sx={{ borderRadius: '50px', bgcolor: 'rgba(255,255,255,0.2)', color: 'white', textTransform: 'none', fontWeight: 600, px: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Email</Button>}
        </Box>
      </Box>
      <Box sx={{ p: 3 }}>
        <SharedSections cardData={cardData} accent={accent} onSaveContact={handleSaveContact} />
      </Box>
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#94a3b8', py: 2 }}>Powered by WebCard Digital</Typography>
    </Box>
  );
};

const PersonalCard = () => {
  const { username } = useParams();
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrPopupOpen, setQrPopupOpen] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const cardRef = useRef(null);

  // SEO Meta Tags Effect
  useEffect(() => {
    if (cardData) {
      const aboutPlain =
        stripAboutFormatting(cardData.about || '') ||
        'Connect with me through my digital business card.';

      // Update document title
      document.title = `${cardData.name} - Digital Business Card | WebCard Digital`;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          `${cardData.name} - ${cardData.title || 'Professional'} at ${cardData.company || 'Business'}. ${aboutPlain}`
        );
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = `${cardData.name} - ${cardData.title || 'Professional'} at ${cardData.company || 'Business'}. ${aboutPlain}`;
        document.head.appendChild(meta);
      }

      // Add Open Graph meta tags
      const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.setAttribute('content', `${cardData.name} - Digital Business Card`);
      if (!document.querySelector('meta[property="og:title"]')) document.head.appendChild(ogTitle);

      const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      ogDescription.setAttribute('content', `${cardData.name} - ${cardData.title || 'Professional'} at ${cardData.company || 'Business'}. ${aboutPlain}`);
      if (!document.querySelector('meta[property="og:description"]')) document.head.appendChild(ogDescription);

      const ogImage = document.querySelector('meta[property="og:image"]') || document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      ogImage.setAttribute('content', cardData.profileImageUrl || 'https://app.webcard.digital/logo192.png');
      if (!document.querySelector('meta[property="og:image"]')) document.head.appendChild(ogImage);

      const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      ogUrl.setAttribute('content', `https://app.webcard.digital/${username}`);
      if (!document.querySelector('meta[property="og:url"]')) document.head.appendChild(ogUrl);

      // Add Twitter Card meta tags
      const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      twitterCard.setAttribute('content', 'summary_large_image');
      if (!document.querySelector('meta[name="twitter:card"]')) document.head.appendChild(twitterCard);

      const twitterTitle = document.querySelector('meta[name="twitter:title"]') || document.createElement('meta');
      twitterTitle.setAttribute('name', 'twitter:title');
      twitterTitle.setAttribute('content', `${cardData.name} - Digital Business Card`);
      if (!document.querySelector('meta[name="twitter:title"]')) document.head.appendChild(twitterTitle);

      const twitterDescription = document.querySelector('meta[name="twitter:description"]') || document.createElement('meta');
      twitterDescription.setAttribute('name', 'twitter:description');
      twitterDescription.setAttribute('content', `${cardData.name} - ${cardData.title || 'Professional'} at ${cardData.company || 'Business'}. ${aboutPlain}`);
      if (!document.querySelector('meta[name="twitter:description"]')) document.head.appendChild(twitterDescription);

      const twitterImage = document.querySelector('meta[name="twitter:image"]') || document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      twitterImage.setAttribute('content', cardData.profileImageUrl || 'https://app.webcard.digital/logo192.png');
      if (!document.querySelector('meta[name="twitter:image"]')) document.head.appendChild(twitterImage);

      // Add JSON-LD structured data
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": cardData.name,
        "jobTitle": cardData.title,
        "worksFor": {
          "@type": "Organization",
          "name": cardData.company
        },
        "email": cardData.email,
        "telephone": cardData.phone,
        "url": `https://app.webcard.digital/${username}`,
        "image": cardData.profileImageUrl,
        "description": aboutPlain,
        "sameAs": [
          cardData.linkedin,
          cardData.twitter,
          cardData.instagram,
          cardData.facebook,
          cardData.website
        ].filter(Boolean)
      };

      // Remove existing structured data
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [cardData, username]);

  useEffect(() => {
    let cancelled = false;

    // Load Google Analytics only for card pages
    const loadGoogleAnalytics = () => {
      if (typeof window !== 'undefined' && !window.gtag) {
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-YC5ERCCTV2';
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-YC5ERCCTV2', {
          page_title: `${username}'s Digital Business Card`,
          page_path: `/${username}`,
        });
      }
    };

    const fetchCard = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load Google Analytics for this card page
        loadGoogleAnalytics();
        
        // Fetch card data from API
        console.log('Fetching card data for username:', username);
        
        const response = await fetch(`https://api.webcard.digital/api/cards/url/${username}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            console.log('Card not found in database');
            if (!cancelled) setError('Card not found');
            return;
          }
          throw new Error('Failed to fetch card data');
        }
        
        const data = await response.json();
        
        // Handle NFC UUID redirect
        if (data.nfcRedirect && data.redirectTo) {
          if (!cancelled) window.location.replace(`/${data.redirectTo}`);
          return;
        }

        if (cancelled) return;
        
        const userCard = data.card;
        
        let cardData;
        
        if (userCard) {
          const cardDataFromAPI = userCard.cardData;
          const d0 = toDisplayCard(cardDataFromAPI);
          const absUrl = (u) =>
            u ? (u.startsWith('http') ? u : `https://api.webcard.digital${u}`) : '';
          cardData = {
            id: userCard.id,
            username: username,
            ...d0,
            profileImageUrl: absUrl(d0.profileImageUrl),
            bannerImageUrl: absUrl(d0.bannerImageUrl),
            template: userCard.templateName || 'template1',
            templateData: userCard.templateData || null,
            gallerySection: d0.gallerySection
              ? {
                  ...d0.gallerySection,
                  images: (d0.gallerySection.images || []).map((img) => ({
                    ...img,
                    url: img.url ? absUrl(img.url) : '',
                  })),
                }
              : { enabled: true, layout: 'grid6', images: [] },
          };
        } else {
          // Fallback to mock data if no saved card found
          cardData = {
            id: username,
            username: username,
            name: username === 'bhavinya' ? 'Bhavinya' : (username === 'anvesh' ? 'Anvesh Prajapati' : 'John Doe'),
            title: username === 'bhavinya' ? 'Relationship Manager' : (username === 'anvesh' ? 'Software Engineer' : 'Marketing Director'),
            company: username === 'bhavinya' ? 'ICICI Bank Ltd' : (username === 'anvesh' ? 'GVM Technologies' : 'Tech Solutions Inc.'),
            email: username === 'bhavinya' ? 'Bhavinya.kumar@icicibank.com' : (username === 'anvesh' ? 'info@gvmtechnologies.com' : 'john.doe@example.com'),
            phone: username === 'bhavinya' ? '+91-8655311695' : '+1 (555) 123-4567',
            website: username === 'bhavinya' ? 'https://gvmtechnologies.com' : '',
            about: username === 'bhavinya' 
              ? 'Specialized in providing 360-degree banking solutions tailor-made for healthcare institutions and professionals.'
              : (username === 'anvesh' 
                ? 'Passionate software engineer with expertise in web development and digital solutions.'
                : 'Creative marketing professional with expertise in digital campaigns and brand strategy.'),
            profileImageUrl: username === 'bhavinya' ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRkY2QjM1Ii8+CjxjaXJjbGUgY3g9IjIwMCIgY3k9IjE4MCIgcj0iNjAiIGZpbGw9IndoaXRlIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iNDgiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSIjRkY2QjM1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5CPC90ZXh0Pgo8L3N2Zz4K' : '',
            bannerImageUrl: username === 'bhavinya' ? 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDgwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI4MDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRkY2QjM1Ii8+Cjx0ZXh0IHg9IjQwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iNDgiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SUlDSSBCYW5rPC90ZXh0Pgo8L3N2Zz4K' : '',
            template: username === 'bhavinya' ? 'template2' : 'template1',
            sections: username === 'bhavinya' ? [
              { id: 'basic-info', name: 'Basic Information', enabled: true, order: 1 },
              { id: 'about-section', name: 'About Section', enabled: true, order: 2 },
              { id: 'contact-info', name: 'Contact Information', enabled: true, order: 3 },
              { id: 'custom-123', name: 'Lets talk', enabled: true, order: 4 }
            ] : [],
            'custom-123_header': username === 'bhavinya' ? 'Lets talk' : '',
            'custom-123_description': username === 'bhavinya' ? 'Hello, lets have discussion' : '',
            linkedin: 'https://linkedin.com/in/' + username,
            twitter: 'https://twitter.com/' + username,
            instagram: 'https://instagram.com/' + username,
            facebook: 'https://facebook.com/' + username,
            profileImage: null,
          };
        }
        
        console.log('Final card data:', cardData);
        console.log('Profile image URL:', cardData.profileImageUrl);
        console.log('Banner image URL:', cardData.bannerImageUrl);
        console.log('Template:', cardData.template);
        console.log('Using fallback data:', !userCard);
        if (cancelled) return;
        setCardData(cardData);
        
        // Track card view with Google Analytics
        if (window.gtag) {
          window.gtag('event', 'card_view', {
            card_id: cardData.id,
            card_owner: cardData.username,
            template: cardData.template,
            event_category: 'engagement',
            event_label: `Card: ${cardData.id}`,
          });
        }

        // Single DB view per load (server GET no longer inserts; see /api/analytics/track-view)
        if (userCard) {
          trackCardView(username);
        }
        
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load card data');
          console.error('Error fetching card:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (username) {
      fetchCard();
    }
    return () => {
      cancelled = true;
    };
  }, [username]);

  const handleShare = () => { // eslint-disable-line no-unused-vars
    if (navigator.share) {
      navigator.share({
        title: `${cardData.name} - Digital Business Card`,
        text: `Connect with ${cardData.name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
    
    // Track share event with Google Analytics
    if (window.gtag) {
      window.gtag('event', 'card_shared', {
        card_id: cardData.id,
        share_method: 'native_share',
        event_category: 'engagement',
        event_label: `Share: native_share`,
      });
    }
  };

  const handleSaveContact = () => {
    const cardSlug = cardData.customUrl || cardData.username || username;
    window.open(`https://api.webcard.digital/api/vcard/${cardSlug}`, '_blank');
    if (window.gtag) {
      window.gtag('event', 'contact_saved', { card_id: cardData.id, event_category: 'conversion' });
    }
  };

  const handleDownload = () => { // eslint-disable-line no-unused-vars
    handleSaveContact();
    
    // Track download event with Google Analytics
    if (window.gtag) {
      window.gtag('event', 'contact_downloaded', {
        card_id: cardData.id,
        event_category: 'conversion',
        event_label: `Download: ${cardData.id}`,
      });
    }
  };

  const generateQRCode = async (url) => {
    try {
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(qrCodeDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleQrCode = () => {
    const cardUrl = window.location.href;
    generateQRCode(cardUrl);
    setQrPopupOpen(true);
  };

  const handleCloseQrPopup = () => {
    setQrPopupOpen(false);
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      alert('Failed to copy URL to clipboard');
    }
  };

  const handleAddToGoogleWallet = async () => {
    try {
      // Generate Google Wallet pass
      const response = await fetch('https://api.webcard.digital/api/cards/generate-google-wallet-pass', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cardUrl: cardData.customUrl || cardData.username
        })
      });

      const data = await response.json();

      if (data.success) {
        // Create Google Wallet URL with the JWT as a direct fallback
        const walletUrl = `https://pay.google.com/gp/v/save/${encodeURIComponent(data.walletPass)}`;
        window.open(walletUrl, '_blank');
      } else {
        alert('Failed to generate Google Wallet pass. Please try again.');
      }
    } catch (error) {
      console.error('Error adding to Google Wallet:', error);
      alert('Error adding to Google Wallet. Please try again.');
    }
  };

  // Removed official Google Wallet JS button rendering; using a single image button fallback

  const handleAddToAppleWallet = async () => { // eslint-disable-line no-unused-vars
    try {
      const cardUrl = cardData.customUrl || cardData.username;
      const url = `https://api.webcard.digital/api/cards/apple-wallet/${cardUrl}`;

      // Always open the GET endpoint in a new tab; browser will handle Wallet handoff
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newTab) {
        // Fallback if popup blocked
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error adding to Apple Wallet:', error);
      alert('Error adding to Apple Wallet. Please try again.');
    }
  };

  const handleAddToHomeScreen = () => {
    // Implement Add to Home Screen
    console.log('Add to Home Screen');
    alert('Add to Home Screen functionality coming soon!');
  };

  // Track card view in database
  const trackCardView = async (cardUrl) => {
    try {
      const ua = navigator.userAgent;
      const isMobile = /Mobi|Android/i.test(ua);
      const isTablet = /Tablet|iPad/i.test(ua);
      const deviceType = isTablet ? 'Tablet' : isMobile ? 'Mobile' : 'Desktop';
      const browserMatch = ua.match(/(Chrome|Safari|Firefox|Edge|Opera|MSIE|Trident)[\/\s]?(\d+)/i);
      const browser = browserMatch ? browserMatch[1].replace('Trident', 'IE') : 'Unknown';
      const osMatch = ua.match(/(Windows NT|Mac OS X|Linux|Android|iOS|iPhone OS)[\s\/]?([0-9._]*)/i);
      const os = osMatch ? osMatch[1].replace(' NT', '').replace(' OS X', '') : 'Unknown';

      await fetch('https://api.webcard.digital/api/analytics/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardUrl,
          userAgent: ua,
          referrer: document.referrer || null,
          deviceType,
          browser,
          os,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      });
    } catch (error) {
      // Silent fail for analytics
    }
  };

  const handleAddToGallery = async () => {
    try {
      // Create a temporary element with the card + QR code for download
      const tempCard = document.createElement('div');
      tempCard.style.position = 'absolute';
      tempCard.style.left = '-9999px';
      tempCard.style.top = '-9999px';
      tempCard.style.width = '400px';
      tempCard.style.backgroundColor = '#ffffff';
      tempCard.style.padding = '20px';
      tempCard.style.borderRadius = '16px';
      tempCard.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      
      // Add banner image if exists
      if (cardData.bannerImageUrl) {
        const bannerWrap = document.createElement('div');
        bannerWrap.style.width = '100%';
        bannerWrap.style.backgroundColor = '#f8f9fa';
        bannerWrap.style.lineHeight = '0';
        bannerWrap.style.overflow = 'hidden';
        bannerWrap.style.borderRadius = '12px 12px 0 0';
        bannerWrap.style.marginBottom = '20px';
        const bannerImg = document.createElement('img');
        bannerImg.src = cardData.bannerImageUrl;
        bannerImg.alt = '';
        bannerImg.style.width = '100%';
        bannerImg.style.height = 'auto';
        bannerImg.style.display = 'block';
        bannerWrap.appendChild(bannerImg);
        tempCard.appendChild(bannerWrap);
      }
      
      // Add profile section
      const profileSection = document.createElement('div');
      profileSection.style.textAlign = 'center';
      profileSection.style.marginBottom = '20px';
      
      // Profile image
      const profileImg = document.createElement('img');
      profileImg.src = cardData.profileImageUrl;
      profileImg.style.width = '80px';
      profileImg.style.height = '80px';
      profileImg.style.borderRadius = '50%';
      profileImg.style.marginBottom = '12px';
      profileImg.style.objectFit = 'cover';
      profileSection.appendChild(profileImg);
      
      // Name
      const nameDiv = document.createElement('div');
      nameDiv.textContent = cardData.name;
      nameDiv.style.fontSize = '24px';
      nameDiv.style.fontWeight = 'bold';
      nameDiv.style.color = '#1a1a1a';
      nameDiv.style.marginBottom = '4px';
      profileSection.appendChild(nameDiv);
      
      // Title
      const titleDiv = document.createElement('div');
      titleDiv.textContent = cardData.title;
      titleDiv.style.fontSize = '16px';
      titleDiv.style.color = '#666';
      titleDiv.style.marginBottom = '2px';
      profileSection.appendChild(titleDiv);
      
      // Company
      const companyDiv = document.createElement('div');
      companyDiv.textContent = cardData.company;
      companyDiv.style.fontSize = '14px';
      companyDiv.style.color = '#666';
      profileSection.appendChild(companyDiv);
      
      tempCard.appendChild(profileSection);
      
      // Add QR code
      const qrDiv = document.createElement('div');
      qrDiv.style.textAlign = 'center';
      qrDiv.style.marginTop = '20px';
      
      const qrImg = document.createElement('img');
      qrImg.src = qrCodeDataUrl;
      qrImg.style.width = '120px';
      qrImg.style.height = '120px';
      qrImg.style.borderRadius = '8px';
      qrDiv.appendChild(qrImg);
      
      tempCard.appendChild(qrDiv);
      
      // Add to DOM temporarily
      document.body.appendChild(tempCard);
      
      // Generate canvas
      const canvas = await html2canvas(tempCard, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      // Remove temporary element
      document.body.removeChild(tempCard);
      
      // Download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${cardData.name}_business_card.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
      
    } catch (error) {
      console.error('Error generating card image:', error);
      alert('Failed to generate card image. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center', color: 'white' }}>
          <CircularProgress sx={{ color: 'white', mb: 2 }} />
          <Typography variant="h6">
            Loading {username}'s card...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!cardData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
          p: 2,
        }}
      >
        <Alert severity="warning" sx={{ maxWidth: 400 }}>
          Card not found for user: {username}
        </Alert>
      </Box>
    );
  }

  // Use CardRenderer config for consistent template styling
  const templateName = cardData.template || 'Classic Professional';
  const cfg = (() => {
    const { TEMPLATE_CONFIGS } = require('../components/CardRenderer');
    return TEMPLATE_CONFIGS[templateName] || TEMPLATE_CONFIGS['Classic Professional'];
  })();
  const accent = cfg.accent || '#00A7E3';
  const layoutType = cfg.layout || 'hero';

  const cardSlug = cardData.customUrl || cardData.username;
  const appleWalletUrl = `https://api.webcard.digital/api/cards/apple-wallet/${cardSlug}`;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa', // Light gray background to highlight the card
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Card ref={cardRef} sx={{ maxWidth: 400, width: '100%', borderRadius: 1, boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <PersonalCardContent cardData={cardData} cfg={cfg} layoutType={layoutType} accent={accent} handleSaveContact={handleSaveContact} />
      </Card>

      {/* Floating Action Buttons */}
      <Box sx={{ position: 'fixed', bottom: 20, left: 20, display: 'flex', flexDirection: 'column', gap: 1.5, zIndex: 1000 }}>
        <Button onClick={handleSaveContact} title="Save Contact"
          sx={{ width: 48, height: 48, minWidth: 48, borderRadius: '50%', bgcolor: accent, color: 'white', '&:hover': { bgcolor: accent, opacity: 0.9 }, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', p: 0 }}>
          <PersonAddIcon sx={{ fontSize: 22 }} />
        </Button>
        <Button onClick={handleQrCode} title="QR Code"
          sx={{ width: 48, height: 48, minWidth: 48, borderRadius: '50%', bgcolor: '#000', color: 'white', '&:hover': { bgcolor: '#333' }, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', p: 0 }}>
          <QrCodeIcon sx={{ fontSize: 20 }} />
        </Button>
        <Button onClick={handleCopyLink} title="Copy Link"
          sx={{ width: 48, height: 48, minWidth: 48, borderRadius: '50%', bgcolor: '#000', color: 'white', '&:hover': { bgcolor: '#333' }, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', p: 0 }}>
          {shareSuccess ? <CheckIcon sx={{ fontSize: 20 }} /> : <CopyIcon sx={{ fontSize: 20 }} />}
        </Button>
      </Box>

      {/* QR Code Popup */}
      {qrPopupOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            p: 2,
          }}
        >
          <Card
            sx={{
              maxWidth: 400,
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              position: 'relative',
              borderRadius: 1,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
          >
            {/* Close Button */}
            <Button
              onClick={handleCloseQrPopup}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                minWidth: 32,
                borderRadius: '50%',
                backgroundColor: '#000',
                color: 'white',
                zIndex: 1,
                p: 0,
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </Button>

            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              {/* Profile Info */}
              <Avatar
                src={cardData.profileImageUrl}
                sx={{
                  width: 80,
                  height: 80,
                  mx: 'auto',
                  mb: 3,
                  border: '3px solid #f0f0f0',
                }}
              >
                {cardData.name ? cardData.name.charAt(0) : 'A'}
              </Avatar>
              
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  color: '#1a1a1a',
                  fontSize: '28px',
                  letterSpacing: '-0.5px'
                }}
              >
                {cardData.name}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4,
                  color: '#666',
                  fontSize: '16px',
                  fontWeight: 400
                }}
              >
                {cardData.title} at {cardData.company}
              </Typography>

              {/* QR Code */}
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  mx: 'auto',
                  mb: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                {qrCodeDataUrl ? (
                  <img
                    src={qrCodeDataUrl}
                    alt="QR Code"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Generating QR Code...
                  </Typography>
                )}
              </Box>

              {/* Add to Wallet Section */}
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 3,
                  color: '#1a1a1a',
                  fontSize: '14px'
                }}
              >
                Add your Digital Business Card to Wallet
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mb: 4, justifyContent: 'center', alignItems: 'stretch' }}>
                {/* Image button opens save URL directly */}
                <Button
                  onClick={handleAddToGoogleWallet}
                  sx={{ 
                    p: 0,
                    borderRadius: '999px',
                    flex: 1,
                    maxWidth: '45%',
                    '&:hover': {
                      opacity: 0.9,
                    }
                  }}
                >
                  <img 
                    src="/wallet-buttons/add_to_google-wallet.png" 
                    alt="Add to Google Wallet"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '999px'
                    }}
                  />
                </Button>

                <Button
                  component="a"
                  href={appleWalletUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    p: 0,
                    borderRadius: 1,
                    flex: 1,
                    maxWidth: '45%',
                    '&:hover': {
                      opacity: 0.9,
                    }
                  }}
                >
                  <img 
                    src="/wallet-buttons/add_to_apple-wallet.png" 
                    alt="Add to Apple Wallet"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }}
                  />
                </Button>
              </Box>

              {/* Additional Options */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <Button
                  onClick={handleAddToHomeScreen}
                  startIcon={
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      📱
                    </Box>
                  }
                  sx={{ 
                    justifyContent: 'flex-start', 
                    textAlign: 'left', 
                    color: '#333',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    py: 1.5,
                    px: 2,
                    backgroundColor: 'transparent',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Add to Home Screen
                </Button>
                
                <Box sx={{ 
                  height: '1px', 
                  backgroundColor: '#e0e0e0', 
                  mx: 2,
                  mb: 0.5
                }} />
                
                <Button
                  onClick={handleAddToGallery}
                  startIcon={
                    <Box sx={{ 
                      width: 24, 
                      height: 24, 
                      backgroundColor: '#f0f0f0', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontSize: '12px'
                    }}>
                      🌸
                    </Box>
                  }
                  sx={{ 
                    justifyContent: 'flex-start', 
                    textAlign: 'left', 
                    color: '#333',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 400,
                    py: 1.5,
                    px: 2,
                    backgroundColor: 'transparent',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: '#f8f9fa',
                    }
                  }}
                >
                  Add to Gallery
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default PersonalCard;
